import * as React from "react";
import { format } from "date-fns";
// react-day-picker's own locale, not date-fns's: the same date-fns locale plus the
// calendar's aria labels (month and year lists, the arrows, each day) in that
// language. The bare date-fns `et` left them in English in an Estonian UI.
import { et } from "react-day-picker/locale";
import { Calendar as CalendarIcon, X } from "lucide-react";
import type { DateRange } from "react-day-picker";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { Calendar } from "./calendar";
import { Button } from "./button";
import { cn } from "../lib/utils";

export interface DateRangeValue {
  from: Date;
  to:   Date;
}

/** A quick-select preset (e.g. "This month"). `getRange` is evaluated lazily on
 *  click so "today"-relative ranges stay fresh. */
export interface DateRangePreset {
  label:    string;
  getRange: () => DateRangeValue;
}

interface DateRangePickerProps {
  value?:        DateRangeValue | null;
  onChange:      (range: DateRangeValue | null) => void;
  /** Quick-select rail shown left of the calendar. Empty = no rail. */
  presets?:      DateRangePreset[];
  locale?:       string;
  placeholder?:  string;
  className?:    string;
  numberOfMonths?: number;
  /** Accessible name of the clear (x) control. Default English "clear". */
  clearLabel?:   string;
}

/**
 * The width below which the two-month calendar stops fitting.
 *
 * Two months side by side plus the 150px preset rail measure 443px, which is
 * 68px wider than a 375px phone. The popover has no width of its own to shrink
 * - `w-auto` means it is whatever the calendar is - so the calendar itself has
 * to give up a month. That is a count, not a style, so a media query cannot do
 * it and this hook has to exist.
 */
const ONE_MONTH_MQ = "(max-width: 560px)";

function useOneMonth(): boolean {
  const query = () => typeof window !== "undefined" && window.matchMedia(ONE_MONTH_MQ).matches;
  const [narrow, setNarrow] = React.useState(query);
  React.useEffect(() => {
    const mq = window.matchMedia(ONE_MONTH_MQ);
    const sync = () => setNarrow(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);
  return narrow;
}

/**
 * Date-range picker - Radix Popover triggering a two-month react-day-picker
 * calendar with a left rail of quick presets. Mirrors the Ajaraam admin range
 * picker (preset rail + range calendar) so the platforms feel identical.
 *
 * On a phone it is one month with the presets above it rather than beside it;
 * see `useOneMonth`.
 */
export function DateRangePicker({
  value, onChange, presets = [], locale, placeholder, className, numberOfMonths = 2,
  clearLabel = "clear",
}: DateRangePickerProps) {
  const oneMonth = useOneMonth();
  const [open, setOpen]   = React.useState(false);
  const [draft, setDraft] = React.useState<DateRange | undefined>(
    value ? { from: value.from, to: value.to } : undefined,
  );
  // First clicked day of an in-progress range. We drive range selection off the
  // clicked day rather than react-day-picker's proposed range, because in v9
  // range mode the first click already returns { from: day, to: day } - which
  // would otherwise commit a single day and close before a second pick.
  const anchorRef = React.useRef<Date | null>(null);

  // Keep the in-progress draft in sync when the committed value changes.
  React.useEffect(() => {
    setDraft(value ? { from: value.from, to: value.to } : undefined);
  }, [value]);

  // Every fresh open starts a new two-click selection.
  React.useEffect(() => {
    if (open) anchorRef.current = null;
  }, [open]);

  // Accept any Estonian locale tag ("et", "et-EE", …) - callers pass either.
  const isEt  = (locale ?? "").toLowerCase().startsWith("et");
  const fmt   = (d: Date) => format(d, isEt ? "dd.MM.yyyy" : "yyyy-MM-dd");
  const label = value ? `${fmt(value.from)} - ${fmt(value.to)}` : "";

  // react-day-picker v9 passes the clicked day as the 2nd arg. Click 1 anchors
  // the start; click 2 completes the range (ordered) and commits.
  function handleSelect(_range: DateRange | undefined, day: Date) {
    if (!anchorRef.current) {
      anchorRef.current = day;
      setDraft({ from: day, to: undefined });
      return;
    }
    const a = anchorRef.current;
    const from = a <= day ? a : day;
    const to   = a <= day ? day : a;
    anchorRef.current = null;
    setDraft({ from, to });
    onChange({ from, to });
    setOpen(false);
  }

  function applyPreset(p: DateRangePreset) {
    const r = p.getRange();
    anchorRef.current = null;
    setDraft({ from: r.from, to: r.to });
    onChange(r);
    setOpen(false);
  }

  function sameRange(a: DateRangeValue, b: DateRangeValue): boolean {
    return fmt(a.from) === fmt(b.from) && fmt(a.to) === fmt(b.to);
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn("justify-start text-left font-normal gap-2", !label && "text-muted-foreground", className)}
        >
          <CalendarIcon className="size-3.5 shrink-0 text-muted-foreground" />
          <span className="truncate tabular-nums">
            {label || placeholder || (isEt ? "Vali vahemik" : "Pick a range")}
          </span>
          {label && (
            <span
              role="button"
              tabIndex={-1}
              onClick={(e) => { e.stopPropagation(); onChange(null); }}
              // 20px is a mouse target. The padding grows the hit area to 40px
              // without moving the pill around it. The red hover disc is drawn on
              // the inner circle, so it stays inside the field at any height, and
              // the hit area takes no focus frame of its own (a click focuses it).
              className="group/clear -my-2.5 -mr-1.5 ml-auto box-content inline-flex size-5 items-center justify-center p-2.5 text-muted-foreground outline-none"
              aria-label={clearLabel}
            >
              <span className="inline-flex size-5 items-center justify-center rounded-full transition-colors group-hover/clear:bg-destructive/15 group-hover/clear:text-destructive">
                <X className="size-3" />
              </span>
            </span>
          )}
        </Button>
      </PopoverTrigger>
      {/* `collisionPadding` is what makes Radix publish a real
          `--radix-popover-content-available-height`; without it the popover
          reports the whole viewport as available and hangs off the bottom of a
          phone. The content is then capped to what is actually there and
          scrolls inside itself. */}
      <PopoverContent
        className="flex w-auto max-w-[calc(100vw-16px)] flex-col overflow-hidden p-0 max-h-[var(--radix-popover-content-available-height)]"
        align="end"
        collisionPadding={8}
      >
        <div className="flex min-h-0 overflow-y-auto max-[560px]:flex-col">
          {presets.length > 0 && (
            // Beside the calendar on a desktop, above it on a phone: a 150px
            // rail plus one month is still 450px, and the presets wrap into two
            // or three short rows where a column would not fit at all.
            <div className="flex w-[150px] max-h-[320px] flex-col gap-1 overflow-y-auto border-r border-border p-2 max-[560px]:max-h-none max-[560px]:w-full max-[560px]:flex-row max-[560px]:flex-wrap max-[560px]:border-b max-[560px]:border-r-0">
              {presets.map((p) => {
                const r = p.getRange();
                const active = value ? sameRange(value, r) : false;
                return (
                  <Button
                    key={p.label}
                    variant={active ? "primary" : "ghost"}
                    size="sm"
                    className="h-8 w-full justify-start px-2 text-xs font-normal max-[560px]:w-auto"
                    onClick={() => applyPreset(p)}
                  >
                    {p.label}
                  </Button>
                );
              })}
            </div>
          )}
          <Calendar
            mode="range"
            numberOfMonths={oneMonth ? 1 : numberOfMonths}
            defaultMonth={draft?.from}
            selected={draft}
            onSelect={handleSelect}
            locale={isEt ? et : undefined}
            autoFocus
          />
        </div>
      </PopoverContent>
    </Popover>
  );
}
